import "react-datepicker/dist/react-datepicker.css";
import { useMemo, useState } from "react";
import { ReportApi } from "../../../services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  ValueFormatterParams,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import useSidebar from "../../../hooks/useSidebar";
import BaseGrid from "../../BaseGrid";
import { downloadFile } from "../../../utils/DownloadFile";
import useReport from "../../../hooks/useReport";
import LoadingComponent from "../../../components/LoadingComponent";
import SearchCriteriaRedReports from "../../../components/SearchCriteriaRedReports";
import { formatCurrency } from "../../../utils/FormatCurrency";
import { TrialBalanceHistoricalDataModel } from "../../../models/TrialBalanceHistoricalDataModel";

const TrialBalanceHistoricalData = () => {
  const { setIsOpen } = useSidebar();  
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);  

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setColumnVisibility();
    setDataSource(createServerSideDatasource());
  };

  const setColumnVisibility = () => {
    const removedColumns = setColumnsToRemove();
    const newColumnDefs = originalColDefs.filter(
      (col) => !removedColumns.includes(col.field ?? "")
    );
    setColumnDefs(newColumnDefs);    
  };

  const setColumnsToRemove = () => {
    let columnsToRemove = [];
    if (reportInputs?.reportToggles?.groupByIca === false)
      columnsToRemove.push("ica");
    if (reportInputs?.reportToggles?.groupByBin === false)
      columnsToRemove.push("bin");
    if (reportInputs?.reportToggles?.groupByProgram === false) {
      columnsToRemove.push("program");
      columnsToRemove.push("programUniqueTag");
    }
    if (reportInputs?.reportToggles?.groupByDate === false)
      columnsToRemove.push("date");
    return columnsToRemove;
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);
    ReportApi.exportTrialBalanceHistoricalData(mapReportRequest(null))
      .then((res) => {
        downloadFile(TrialBalanceHistoricalData.displayName, res.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const currencyFormatter = (
    params: ValueFormatterParams<TrialBalanceHistoricalDataModel, any>
  ) => {
    if (params.value == undefined || params.data == undefined) return "";
    const cur = formatCurrency(params.value, "USD");
    if (params.value < 0) {
      const formattedCur = cur + "";
      return "(" + formattedCur.replace("-", "") + ")";
    }
    return cur + "";
  };

  const [originalColDefs] = useState<ColDef<TrialBalanceHistoricalDataModel>[]>(
    [
      { headerName: "ICA", field: "ica" },
      { field: "bin" },
      { field: "program" },
      { field: "date" },
      { field: "transferCardCount" },
      {
        field: "transferAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "issuedCardCount" },
      {
        field: "issuedAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "reloadCardCount" },
      {
        field: "reloadCardAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "feePaymentAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "otherAchFunds",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "totalCardFund",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "adminFees",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "receivedSettlementCount" },
      {
        field: "receivedSettlementAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "transferredSettlementAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "unloadCardCount" },
      {
        field: "unloadCardAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "atmTransactionCount" },
      {
        field: "atmTransactionAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Cardholder Fees",
        field: "cardholderFeeAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "balanceInquiryAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Other Non-Cardholder Settlement Amount",
        field: "unpostedSettlementAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "manualAdjustmentAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "accountMaintenanceAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "taxAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "distributorRefundAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "expiredFundsAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        field: "expiredFundsTaxAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "unloadToAchCount" },
      {
        headerName: "Unload To Ach Amount",
        field: "unloadToAchAmounts",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Unload To Ach Fee Amount",
        field: "unloadToAchFeeAmounts",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "directPayCount" },
      {
        field: "directPayAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "loadMobileWalletCount" },
      {
        field: "loadMobileWalletAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "unloadMobileWalletCount" },
      {
        field: "unloadMobileWalletAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "delegationUnloadCount" },
      {
        field: "delegationUnloadAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "negativeBalanceAdjustmentCount" },
      {
        field: "negativeBalanceAdjustmentAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "consumerLoadFundsCount" },
      {
        field: "consumerLoadFundsAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "partnerUnloadCount" },
      {
        field: "partnerUnloadAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "extendAccountExpirationLoadCount" },
      {
        field: "extendAccountExpirationLoadAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "expressTransferCount" },
      {
        field: "expressTransferAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "achDebitCount" },
      {
        field: "achDebitAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { field: "achDirectDepositSettledCount" },
      {
        field: "achDirectDepositSettledAmount",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Account Balance",
        field: "balance",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Recaptured Amount",
        field: "recaptureAmounts",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Derecognized Amount",
        field: "derecognitionAmounts",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      {
        headerName: "Account Balance With Derecognition",
        field: "balanceWithDerec",
        valueFormatter: currencyFormatter,
        cellClassRules: {
          "cell-negative-value": (params) => params.value < 0, // Apply 'ag-cell-red' if value is negative
        },
      },
      { headerName: "UniqueTag", field: "programUniqueTag" },
    ]
  );

  const [colDefs, setColumnDefs] =
    useState<ColDef<TrialBalanceHistoricalDataModel>[]>(originalColDefs);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 90,
      defaultMaxWidth: 150,
      skipHeader: false,
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getTrialBalanceHistoricalData(mapReportRequest(params))
      .then((res) => {
        const result = res.data;        
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }        
      })
      .catch(() => {
        params?.fail();
      });
  };  

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Trial Balance Historical Data</h5>
          {(reportInputs?.icaValue?.length ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
            useIcas={true}
            useBins={true}
            usePrograms={true}
            useToggles={true}
            useBankIds={true}
            useDisbursementAccountFundingTypes={true}
          />         
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

TrialBalanceHistoricalData.displayName = "TrialBalanceHistoricalData";

export default TrialBalanceHistoricalData;
